/**
 * Tiny analytics client.
 *
 * Posts events to /viewer/event so the loan officer can later see what each
 * viewer did. Best-effort, fire-and-forget: never blocks the page on a
 * failed network call.
 *
 * Viewer id: anonymous, persisted in localStorage so the same browser is
 * recognised across sessions (used to group history per visitor without
 * requiring sign-in).
 *
 * Session id: a new uuid per page load so we can group choices into a single
 * viewing session.
 */

const API_BASE = import.meta.env.VITE_API_BASE as string;
const VIEWER_KEY = "brv-viewer-id";
const SESSION_KEY = "brv-session-id";

function readOrCreate(key: string, store: Storage): string {
  try {
    let v = store.getItem(key);
    if (!v) {
      v = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      store.setItem(key, v);
    }
    return v;
  } catch {
    return "anon";
  }
}

function getViewerId(): string {
  if (typeof window === "undefined") return "anon";
  return readOrCreate(VIEWER_KEY, window.localStorage);
}

function getSessionId(): string {
  if (typeof window === "undefined") return "no-session";
  return readOrCreate(SESSION_KEY, window.sessionStorage);
}

export type ViewerEventType =
  | "page_view"
  | "video_started"
  | "branch_choice"
  | "video_ended";

interface RecordPayload {
  lo_id: string;
  web_app_id?: string | null;
  agent_slug?: string | null;
  event_type: ViewerEventType;
  video_id?: string | null;
  timestamp_in_video?: number | null;
  question?: string | null;
  choice_label?: string | null;
  branch_video_id?: string | null;
}

/**
 * Fire-and-forget event post. Uses sendBeacon when available so the event
 * still ships if the page is unloading (useful for video_ended on tab close).
 */
export function recordEvent(p: RecordPayload): void {
  if (!p.lo_id) return;
  const body = JSON.stringify({
    ...p,
    viewer_id: getViewerId(),
    session_id: getSessionId(),
  });
  const url = `${API_BASE}/viewer/event`;

  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch { /* fall through to fetch */ }

  try {
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      cache: "no-store",
    });
  } catch { /* swallow */ }
}
